/**
 * VectorToolCard — Card 4 in the masterplan's per-layer Tool Cards.
 *
 * Composition shell that wires the WP-2 `SceneSelection` contract, the
 * `MapLibreAdapter` style hooks (added in this WP), the `terra-draw`
 * plugin (consumed through `usePluginStore.setActivePlugin`), and the
 * scoped `vectorClient.exportSelection` mock into one Material 3 card.
 *
 * The card respects the gTwin read-only permission flag: when set, all
 * edit / draw / export controls render disabled with a `lock` icon and
 * the playbook tooltip "Read-only by project permission".
 *
 * Source-of-truth citations:
 *   - docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md §7.4, §8.4
 *   - geosoft-platform-design-system/docs/DESIGN_POLICY.md §5
 *   - geosoft-platform-design-system/docs/ICON_SYSTEM_PLAYBOOK.md
 *   - frontend/src/app/sceneSelection.ts (WP-2)
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Layer } from '@geosoft/viewer-sdk';
import {
  vectorClient,
  type VectorExportFeature,
  type VectorExportFormat,
} from '@geosoft/viewer-sdk';
import {
  Banner,
  Button,
  Card,
  Chip,
  Icon,
  IconButton,
  Stack,
  Text,
  Tooltip,
  tokens,
} from '@/design';
import { dispatchToolCardRequest, selectByLayerId } from '@/app/sceneSelection';
import { useViewerStore } from '@/state/useViewerStore';
import { usePluginStore } from '@/state/usePluginStore';
import { VectorStylePanel } from './VectorStylePanel';
import type { VectorAttributeRow } from './VectorAttributeTable';
import { VectorAttributeTableDialog } from './VectorAttributeTableDialog';
import { VectorViewInfoPanel } from './VectorViewInfoPanel';
import { loadVectorAttributeFeatures } from './loadVectorAttributeFeatures';
import {
  buildIdentifySnapshot,
  matchIdentifyFeature,
  resolveVectorGeometryKind,
  type IdentifySnapshot,
} from './vectorIdentifyModel';
import {
  resolveVectorHighlightTarget,
  type VectorHighlightTarget,
} from './vectorIdentifyHighlight';
import { editCursorForTool, type VectorEditTool } from './vectorEditMode';
import {
  boundsFromVectorFeatures,
  type VectorFeatureBounds,
} from './vectorFeatureBounds';
import { ScaleBoxOverlay } from './ScaleBoxOverlay';
import {
  commitVectorScaleEdit,
  getVectorScaleEdit,
  hasVectorScaleEdit,
  undoVectorScaleEdit,
  useVectorScaleSessionVersion,
} from './vectorScaleSession';
import { toLonLatGeometry, type ScaleSourceFeature } from './scaleFeatureSource';
import {
  commitVectorSessionEdit,
  getVectorSessionFeaturesForLayer,
  mergeVectorSessionFeatures,
  useVectorEditSessionVersion,
  type VectorEditResult,
} from './vectorEditSession';
import {
  attributeColumnsFromFeatures,
  sortAttributeRowsByObjectId,
  withCoordinateColumns,
} from './vectorAttributeRows';
import {
  DEFAULT_VECTOR_FILTER,
  deriveVectorSourceInfo,
  featureMatchesFilter,
  isVectorToolCardLayer,
  vectorFilterToMaplibre,
  vectorStyleFromLayer,
  vectorStyleToPaintDelta,
  type VectorFilter,
  type VectorStyleState,
  type MaplibreVectorPaintDelta,
} from './vectorStyleModel';

// ---------------------------------------------------------------------------
// Style-hook seam (thin pass-through; mirrors WP-4 adapter setters)
// ---------------------------------------------------------------------------

/**
 * Minimal, structural shape the ToolCard expects from any engine
 * adapter. Mirrors the WP-4 thin pass-through setters added on
 * `MapLibreAdapter`. Keeping the shape structural (not nominal) means
 * future engines (Cesium, deck.gl) can implement the same hooks
 * without a cyclic dependency on the chrome.
 */
export interface VectorStyleHook {
  setVectorPaint?: (layerId: string, delta: MaplibreVectorPaintDelta) => void;
  setVectorFilter?: (layerId: string, filter: unknown) => void;
  setVectorLabels?: (
    layerId: string,
    options: { readonly enabled: boolean; readonly field: string | null },
  ) => void;
  getVectorFeatures?: (layerId: string) => ReadonlyArray<VectorExportFeature>;
  setVectorHighlight?: (layerId: string, featureId: VectorHighlightTarget) => void;
  zoomToVectorBounds?: (bounds: VectorFeatureBounds) => void;
  setVectorFeatureVisible?: (
    layerId: string,
    featureId: string | number,
    visible: boolean,
    properties?: Readonly<Record<string, unknown>>,
  ) => void;
  setIdentifyCursor?: (armed: boolean) => void;
  setVectorEditMode?: (
    layerId: string,
    tool: VectorEditTool | null,
    geometryKind?: import('./vectorIdentifyModel').IdentifyGeometryKind,
    features?: ReadonlyArray<VectorExportFeature>,
    selectedFeatureId?: string | number | null,
    onEdit?: (result: VectorEditResult) => void,
  ) => void;
  deleteSelectedVectorFeature?: (
    layerId: string,
    selectedFeatureId?: string | number | null,
    onEdit?: (result: VectorEditResult) => void,
  ) => void;
  /**
   * Remove one specific session edit from the engine's persistent working-copy
   * overlay. Unlike `deleteSelectedVectorFeature` this never consults the
   * engine's own selection, so the ToolCard's "delete this polygon" row acts on
   * exactly the feature the operator picked.
   */
  removeVectorSessionFeature?: (layerId: string, featureId: string | number) => void;
  setVectorEditCursor?: (cursor: string) => void;
}

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

export interface VectorToolCardProps {
  /** Vector layer selected for inspection. */
  readonly layer: Layer | null;
  /**
   * Read-only flag from the gTwin permission model. Default reads
   * `useViewerStore.readOnly`; provided explicitly for tests and for
   * embeds that need a per-card override (e.g. share-link read-only).
   */
  readonly readOnly?: boolean;
  /** Engine style-hook seam — optional so the card degrades to a UI-only mock. */
  readonly engine?: VectorStyleHook | null;
  /** Feature collection override (otherwise pulled from `engine.getVectorFeatures`). */
  readonly features?: ReadonlyArray<VectorExportFeature>;
  /** Currently selected feature id, used for visual sync + scene-tree dispatch. */
  readonly selectedFeatureId?: string | number | null;
  /** Map host — Scale draws its box here (Giro3D twins have no MapLibre layers). */
  readonly mapContainer?: HTMLElement | null;
  readonly 'data-testid'?: string;
}

export function VectorToolCard({
  layer,
  readOnly: readOnlyProp,
  engine,
  features: featuresProp,
  selectedFeatureId = null,
  mapContainer = null,
  'data-testid': testId = 'vector-tool-card',
}: VectorToolCardProps) {
  const storeReadOnly = useViewerStore((state) => state.readOnly);
  const sceneSelection = useViewerStore((state) => state.sceneSelection);
  const setActivePlugin = usePluginStore((state) => state.setActivePlugin);
  const setSceneSelection = useViewerStore((state) => state.setSceneSelection);
  const storedStyle = useViewerStore((state) =>
    layer ? state.vectorStyleOverrides[layer.id] : undefined,
  );
  const setVectorStyleOverride = useViewerStore((state) => state.setVectorStyleOverride);

  const readOnly = readOnlyProp ?? storeReadOnly;
  const sectionId = useId();

  const [style, setStyle] = useState<VectorStyleState>(() =>
    vectorStyleFromLayer(layer, storedStyle),
  );
  const [filter, setFilter] = useState<VectorFilter>(DEFAULT_VECTOR_FILTER);
  const [attributeOpen, setAttributeOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<VectorExportFormat>('geojson');
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const [fetchedFeatures, setFetchedFeatures] = useState<ReadonlyArray<VectorExportFeature>>([]);
  const [attributeState, setAttributeState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [attributeError, setAttributeError] = useState<string | null>(null);
  const [tableSelectedFeatures, setTableSelectedFeatures] = useState<
    ReadonlyArray<VectorAttributeRow>
  >([]);
  const [identifyArmed, setIdentifyArmed] = useState(false);
  const [identifySnapshot, setIdentifySnapshot] = useState<IdentifySnapshot | null>(null);
  const [editTool, setEditTool] = useState<VectorEditTool | null>(null);
  const [pendingEditTool, setPendingEditTool] = useState<VectorEditTool | null>(null);
  const [editNotice, setEditNotice] = useState<string | null>(null);
  const [lastScaledFeatureId, setLastScaledFeatureId] = useState<string | number | null>(null);
  const scaleSessionVersion = useVectorScaleSessionVersion();
  const editSessionVersion = useVectorEditSessionVersion();

  const layerIsVector = isVectorToolCardLayer(layer);

  // ----- Features -----
  const engineFeatures = useMemo<ReadonlyArray<VectorExportFeature>>(() => {
    if (featuresProp) return featuresProp;
    if (!layer || !engine?.getVectorFeatures) return [];
    try {
      return engine.getVectorFeatures(layer.id) ?? [];
    } catch (err) {
      console.warn('[VectorToolCard] getVectorFeatures threw', err);
      return [];
    }
  }, [engine, featuresProp, layer]);

  useEffect(() => {
    if (featuresProp || !layer || !layerIsVector) {
      setFetchedFeatures([]);
      setAttributeState('idle');
      setAttributeError(null);
      return;
    }
    let cancelled = false;
    setAttributeState('loading');
    setAttributeError(null);
    void loadVectorAttributeFeatures(layer)
      .then((rows) => {
        if (cancelled) return;
        setFetchedFeatures(rows);
        setAttributeState('idle');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFetchedFeatures([]);
        setAttributeState('error');
        setAttributeError(err instanceof Error ? err.message : 'Failed to load features.');
      });
    return () => {
      cancelled = true;
    };
  }, [featuresProp, layer, layerIsVector]);

  const features = useMemo<ReadonlyArray<VectorExportFeature>>(() => {
    const source = fetchedFeatures.length > 0 ? fetchedFeatures : engineFeatures;
    const scaled = sortAttributeRowsByObjectId(
      source.map((feature) => {
        const rawId = feature.id ?? feature.properties?.id;
        const id =
          typeof rawId === 'string' || typeof rawId === 'number' ? rawId : undefined;
        const edit = layer ? getVectorScaleEdit(layer.id, id) : null;
        return withCoordinateColumns(
          edit ? { ...feature, geometry: edit.feature.geometry } : feature,
        );
      }),
    );
    return layer ? mergeVectorSessionFeatures(layer.id, scaled) : scaled;
  }, [editSessionVersion, engineFeatures, fetchedFeatures, layer, scaleSessionVersion]);

  const geometryKind = useMemo(
    () =>
      resolveVectorGeometryKind({
        styleGeometryType: layer?.style?.['geometryType'],
        features,
      }),
    [features, layer?.style],
  );

  const attributeKeys = useMemo(
    () => attributeColumnsFromFeatures(features),
    [features],
  );

  const filteredFeatures = useMemo<ReadonlyArray<VectorAttributeRow>>(() => {
    if (!filter.attribute) return features;
    return features.filter((feature) =>
      featureMatchesFilter(feature.properties ?? {}, filter),
    );
  }, [features, filter]);
  const filteredBounds = useMemo(
    () => boundsFromVectorFeatures(filteredFeatures),
    [filteredFeatures],
  );
  const tableSelectedFeatureIds = useMemo(
    () =>
      tableSelectedFeatures
        .map(featureHighlightId)
        .filter((id): id is string | number => id != null),
    [tableSelectedFeatures],
  );

  useEffect(() => {
    setIdentifyArmed(false);
    setIdentifySnapshot(null);
    setEditTool(null);
    setPendingEditTool(null);
    setEditNotice(null);
    setLastScaledFeatureId(null);
    setTableSelectedFeatures([]);
  }, [layer?.id]);

  useEffect(() => {
    setStyle(vectorStyleFromLayer(layer, storedStyle));
  }, [layer, storedStyle]);

  useEffect(() => {
    if (!identifyArmed || !layer) return;
    if (sceneSelection.source !== 'engine-pick') return;
    if (sceneSelection.layerId && sceneSelection.layerId !== layer.id) return;
    if (sceneSelection.featureId == null && !sceneSelection.featureProperties) return;
    const matched = matchIdentifyFeature(
      features,
      sceneSelection.featureId,
      sceneSelection.featureProperties,
    );
    setIdentifySnapshot(
      buildIdentifySnapshot({
        layerLabel: layer.label ?? layer.id,
        featureId: sceneSelection.featureId ?? matched?.id ?? null,
        properties: {
          ...(matched?.properties ?? {}),
          ...(sceneSelection.featureProperties ?? {}),
        },
        geometry: matched?.geometry,
        clickPoint: sceneSelection.pickPoint ?? null,
      }),
    );
  }, [features, identifyArmed, layer, sceneSelection]);

  // ----- Style propagation -----
  useEffect(() => {
    if (!layer || !engine?.setVectorPaint) return;
    try {
      engine.setVectorPaint(layer.id, vectorStyleToPaintDelta(style));
    } catch (err) {
      console.warn('[VectorToolCard] setVectorPaint failed', err);
    }
  }, [engine, layer, style]);

  useEffect(() => {
    if (!layer || !engine?.setVectorLabels) return;
    try {
      engine.setVectorLabels(layer.id, {
        enabled: style.labelsEnabled,
        field: style.labelField,
      });
    } catch (err) {
      console.warn('[VectorToolCard] setVectorLabels failed', err);
    }
  }, [engine, layer, style.labelField, style.labelsEnabled]);

  useEffect(() => {
    if (!layer || !engine?.setVectorFilter) return;
    try {
      engine.setVectorFilter(layer.id, vectorFilterToMaplibre(filter));
    } catch (err) {
      console.warn('[VectorToolCard] setVectorFilter failed', err);
    }
  }, [engine, filter, layer]);

  useEffect(() => {
    engine?.setIdentifyCursor?.(identifyArmed);
  }, [engine, identifyArmed]);

  useEffect(() => {
    const cursor = editCursorForTool(editTool);
    engine?.setVectorEditCursor?.(cursor);
    return () => engine?.setVectorEditCursor?.('');
  }, [engine, editTool]);

  useEffect(() => {
    if (!layer || !engine?.setVectorHighlight) return;
    const target = resolveVectorHighlightTarget({
      identifyArmed,
      identifyFeatureId: identifySnapshot?.featureId,
      tableSelectedFeatureIds,
      filter,
    });
    try {
      engine.setVectorHighlight(layer.id, target);
    } catch (err) {
      console.warn('[VectorToolCard] setVectorHighlight failed', err);
    }
  }, [
    engine,
    filter,
    identifyArmed,
    identifySnapshot?.featureId,
    layer,
    tableSelectedFeatureIds,
  ]);

  // ----- Actions -----
  const handleSelectFeature = useCallback(
    (feature: VectorAttributeRow) => {
      if (!layer) return;
      const featureId = feature.id ?? feature._rowKey;
      const selection = selectByLayerId(layer.id, {
        layerKind: layer.kind,
        featureId,
        source: 'tool-card',
      });
      setSceneSelection(selection);
      dispatchToolCardRequest(selection);
    },
    [layer, setSceneSelection],
  );

  const handleVectorEditResult = useCallback(
    (result: VectorEditResult) => {
      if (!layer) return;
      const normalizedFeature = result.feature
        ? {
            ...result.feature,
            id: result.feature.id ?? result.featureId,
            geometry: toLonLatGeometry(result.feature.geometry),
          }
        : undefined;
      const featureId =
        result.featureId
        ?? normalizedFeature?.id
        ?? normalizedFeature?.properties?.id;
      const normalized: VectorEditResult = {
        operation: result.operation,
        feature: normalizedFeature,
        featureId:
          typeof featureId === 'string' || typeof featureId === 'number'
            ? featureId
            : undefined,
      };
      commitVectorSessionEdit(layer.id, normalized, {
        strokeColor: style.strokeColor,
        strokeWidth: style.strokeWidth,
        fillColor: style.fillColor,
        fillOpacity: style.fillOpacity,
      });
      if (
        (result.operation === 'update' || result.operation === 'delete')
        && normalized.featureId != null
      ) {
        const original = features.find(
          (feature) => String(feature.id ?? feature.properties?.id) === String(normalized.featureId),
        );
        engine?.setVectorFeatureVisible?.(
          layer.id,
          normalized.featureId,
          false,
          original?.properties,
        );
      }
      setEditNotice(
        result.operation === 'create'
          ? 'Polygon created in this session.'
          : result.operation === 'update'
            ? 'Polygon geometry updated in this session.'
            : 'Selected polygon deleted from this session.',
      );
    },
    [engine, features, layer, style],
  );

  const handleEditTool = useCallback(
    (tool: VectorEditTool) => {
      if (readOnly || !layer) return;
      if (
        (tool === 'draw' || tool === 'trace')
        && geometryKind === 'unknown'
      ) {
        const nextPending = pendingEditTool === tool ? null : tool;
        setPendingEditTool(nextPending);
        setEditTool(null);
        setEditNotice(
          nextPending
            ? 'Preparing the layer geometry. Drawing will start automatically when it is ready.'
            : null,
        );
        return;
      }
      if (tool === 'delete') {
        engine?.deleteSelectedVectorFeature?.(
          layer.id,
          selectedFeatureId,
          handleVectorEditResult,
        );
        setEditTool(null);
        engine?.setVectorEditMode?.(layer.id, null);
        return;
      }
      setPendingEditTool(null);
      const next = editTool === tool ? null : tool;
      setEditTool(next);
      if (next) {
        setIdentifyArmed(false);
        setIdentifySnapshot(null);
        engine?.setIdentifyCursor?.(false);
        engine?.setVectorHighlight?.(layer.id, null);
      }
      setEditNotice(null);
      engine?.setVectorEditMode?.(
        layer.id,
        next,
        geometryKind,
        features,
        selectedFeatureId,
        handleVectorEditResult,
      );
    },
    [
      editTool,
      engine,
      features,
      geometryKind,
      handleVectorEditResult,
      layer,
      pendingEditTool,
      readOnly,
      selectedFeatureId,
    ],
  );

  useEffect(() => {
    if (!pendingEditTool || geometryKind === 'unknown') return;
    const tool = pendingEditTool;
    setPendingEditTool(null);
    handleEditTool(tool);
  }, [geometryKind, handleEditTool, pendingEditTool]);

  const handleScaleCommit = useCallback(
    (feature: ScaleSourceFeature) => {
      if (!layer) return;
      const featureId = feature.id ?? selectedFeatureId;
      if (featureId == null || !feature.geometry) return;
      commitVectorScaleEdit({
        layerId: layer.id,
        featureId,
        feature,
        strokeColor: style.strokeColor,
        strokeWidth: style.strokeWidth,
        fillColor: style.fillColor,
        fillOpacity: style.fillOpacity,
      });
      setLastScaledFeatureId(featureId);
      engine?.setVectorFeatureVisible?.(
        layer.id,
        featureId,
        false,
        feature.properties,
      );
    },
    [engine, layer, selectedFeatureId, style],
  );

  const undoFeatureId = lastScaledFeatureId ?? selectedFeatureId;
  const canUndoScale = Boolean(
    layer && hasVectorScaleEdit(layer.id, undoFeatureId),
  );
  const handleUndoScale = useCallback(() => {
    if (!layer || undoFeatureId == null) return;
    const current = getVectorScaleEdit(layer.id, undoFeatureId);
    const remaining = undoVectorScaleEdit(layer.id, undoFeatureId);
    engine?.setVectorFeatureVisible?.(
      layer.id,
      undoFeatureId,
      remaining == null,
      current?.feature.properties,
    );
  }, [engine, layer, undoFeatureId]);

  /**
   * Every polygon this session created or reshaped, in a stable order, with a
   * human label. This is the list the operator deletes from — picking a row is
   * unambiguous, unlike map-click selection which cannot distinguish two
   * overlapping sketches.
   */
  const sessionEdits = useMemo(() => {
    if (!layer) return [];
    void editSessionVersion;
    let drawn = 0;
    let reshaped = 0;
    return getVectorSessionFeaturesForLayer(layer.id).map((entry) => {
      const rawId = entry.feature.id ?? entry.feature.properties?.id;
      const id = typeof rawId === 'string' || typeof rawId === 'number' ? rawId : null;
      const created = entry.operation === 'create';
      if (created) drawn += 1;
      else reshaped += 1;
      return {
        id,
        feature: entry.feature,
        operation: entry.operation,
        label: created ? `Drawn polygon ${drawn}` : `Reshaped polygon ${reshaped}`,
      };
    });
  }, [editSessionVersion, layer]);

  const handleDeleteSessionEdit = useCallback(
    (featureId: string | number, operation: 'create' | 'update') => {
      if (!layer || readOnly) return;
      commitVectorSessionEdit(
        layer.id,
        { operation: 'delete', featureId },
        {
          strokeColor: style.strokeColor,
          strokeWidth: style.strokeWidth,
          fillColor: style.fillColor,
          fillOpacity: style.fillOpacity,
        },
      );
      engine?.removeVectorSessionFeature?.(layer.id, featureId);
      // A reshaped feature came from the source layer, so undoing the session
      // edit must bring the original geometry back into view.
      if (operation === 'update') {
        engine?.setVectorFeatureVisible?.(layer.id, featureId, true);
      }
      setEditNotice(
        operation === 'create'
          ? 'Drawn polygon deleted.'
          : 'Reshape discarded; the original polygon is back.',
      );
    },
    [engine, layer, readOnly, style],
  );

  const handleDeleteAllSessionEdits = useCallback(() => {
    if (!layer || readOnly || sessionEdits.length === 0) return;
    for (const entry of sessionEdits) {
      if (entry.id == null) continue;
      handleDeleteSessionEdit(entry.id, entry.operation);
    }
    setEditNotice('All session edits deleted.');
  }, [handleDeleteSessionEdit, layer, readOnly, sessionEdits]);

  const handleZoomToSessionEdit = useCallback(
    (feature: VectorExportFeature) => {
      const bounds = boundsFromVectorFeatures([feature]);
      if (bounds) engine?.zoomToVectorBounds?.(bounds);
    },
    [engine],
  );

  const handleSwitchToMeasure = useCallback(() => {
    setActivePlugin('measurement');
  }, [setActivePlugin]);

  const handleExport = useCallback(() => {
    if (!layer) return;
    const exportable = filteredFeatures.length > 0 ? filteredFeatures : features;
    if (exportable.length === 0) {
      setExportNotice('Nothing to export — clear the filter or select features.');
      return;
    }
    const result = vectorClient.exportSelection({
      layerId: layer.id,
      features: exportable,
      format: exportFormat,
    });
    setExportNotice(
      `Exported ${exportable.length} features to ${result.filename} (${result.format}).`,
    );
  }, [exportFormat, features, filteredFeatures, layer]);

  // ----- Render -----
  if (!layer) {
    return (
      <Card level={2} header="Vector tools" data-testid={`${testId}-empty`}>
        <Stack direction="row" align="center" gap={2}>
          <Icon name="info" size={20} decorative />
          <Text variant="body" as="p" style={{ margin: 0 }}>
            Select a vector layer to access symbology, filters, and the attribute table.
          </Text>
        </Stack>
      </Card>
    );
  }

  if (!layerIsVector) {
    return (
      <Card level={2} header="Vector tools" data-testid={`${testId}-disabled`}>
        <Banner
          severity="info"
          title="Not a vector layer"
          body={
            <>
              <code style={{ fontFamily: tokens.font.family.mono }}>{layer.kind}</code>
              {' '}layers use a different ToolCard. Switch to a vector or feature-table layer to use these controls.
            </>
          }
        />
      </Card>
    );
  }

  const sourceInfo = deriveVectorSourceInfo(layer);
  const headerLabel = `${layer.label ?? layer.id} · vector tools`;

  return (
    <div
      data-testid={`${testId}-scroll`}
      style={{
        flexShrink: 0,
        minHeight: 0,
        width: '100%',
        overflowY: 'auto',
        maxHeight: '100%',
      }}
    >
    <ScaleBoxOverlay
      layerId={layer.id}
      features={features}
      selectedFeatureId={selectedFeatureId}
      container={mapContainer}
      armed={editTool === 'scale'}
      onCommit={handleScaleCommit}
    />
    <Card level={2} header={headerLabel} data-testid={testId}>
      <Stack direction="column" gap={3}>
        {/* Layer summary */}
        <Stack direction="row" gap={1} style={{ flexWrap: 'wrap' }}>
          <Chip icon="polyline" label={layer.kind} variant="neutral" />
          <Chip icon="language" label={layer.crs} variant="neutral" />
          <Chip icon={sourceInfo.kind === 'pmtiles' ? 'public' : 'info'} label={sourceInfo.kind} variant="neutral" />
          {readOnly ? (
            <Tooltip content="Read-only by project permission">
              <span>
                <Chip icon="lock" label="Read-only" variant="warning" />
              </span>
            </Tooltip>
          ) : null}
          {filter.attribute ? (
            <Chip icon="filter_list" label={`filter: ${filter.attribute}`} variant="neutral" />
          ) : null}
        </Stack>

        {/* Style */}
        <SectionBlock id={`${sectionId}-style`}>
          <VectorStylePanel
            state={style}
            geometryKind={geometryKind}
            availableLabelFields={attributeKeys}
            features={features}
            readOnly={readOnly}
            onChange={(next) => {
              setStyle(next);
              if (layer) setVectorStyleOverride(layer.id, next);
            }}
            data-testid={`${testId}-style`}
          />
        </SectionBlock>

        {/* Filter row (attribute equals / contains / isEmpty) */}
        <SectionBlock id={`${sectionId}-filter`}>
          <FilterRow
            availableAttributes={attributeKeys}
            value={filter}
            onChange={setFilter}
            readOnly={readOnly}
            testId={`${testId}-filter`}
          />
          <Text variant="caption" as="span" style={{ color: tokens.color.ink.secondary }}>
            Matching <strong>{filteredFeatures.length}</strong> of {features.length} features.
          </Text>
          {filter.attribute ? (
            <Button
              variant="secondary"
              disabled={!filteredBounds || !engine?.zoomToVectorBounds}
              onClick={() => {
                if (filteredBounds) engine?.zoomToVectorBounds?.(filteredBounds);
              }}
              data-testid={`${testId}-zoom-to-filter`}
            >
              <Icon name="zoom_in" size={20} decorative />
              <span>Zoom to matches</span>
            </Button>
          ) : null}
        </SectionBlock>

        {/* Select / query / attribute-table toggle */}
        <SectionBlock id={`${sectionId}-actions`}>
          <Stack direction="row" gap={2} style={{ flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              onClick={() => {
                const first = filteredFeatures[0];
                if (first) handleSelectFeature(first);
              }}
              data-testid={`${testId}-select-first`}
            >
              <Icon name="select_all" size={20} decorative />
              <span>Select first match</span>
            </Button>
            <Button
              variant="secondary"
              onClick={() => setAttributeOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={attributeOpen}
              data-testid={`${testId}-toggle-attributes`}
            >
              <Icon name="table" size={20} decorative />
              <span>Open attribute table</span>
            </Button>
          </Stack>
        </SectionBlock>

        <SectionBlock id={`${sectionId}-view-info`}>
          <VectorViewInfoPanel
            armed={identifyArmed}
            snapshot={identifySnapshot}
            onArm={() => setIdentifyArmed(true)}
            onClear={() => {
              setIdentifyArmed(false);
              setIdentifySnapshot(null);
            }}
            data-testid={`${testId}-view-info`}
          />
        </SectionBlock>

        {attributeOpen ? (
          <VectorAttributeTableDialog
            key={layer.id}
            title={layer.label ?? layer.id}
            onClose={() => setAttributeOpen(false)}
            features={filteredFeatures as ReadonlyArray<VectorAttributeRow>}
            columns={attributeKeys}
            idColumns={['id', 'ID', 'gid', 'osm_id', 'objectid', 'OBJECTID', 'OBJECTID_1']}
            selectedFeatureId={selectedFeatureId}
            state={
              attributeState === 'loading' && features.length === 0
                ? 'loading'
                : attributeState === 'error' && features.length === 0
                  ? 'error'
                  : features.length === 0
                    ? 'empty'
                    : 'idle'
            }
            errorMessage={attributeError ?? undefined}
            onSelect={handleSelectFeature}
            onSelectionChange={setTableSelectedFeatures}
            onZoomToRows={(rows) => {
              const bounds = boundsFromVectorFeatures(rows);
              if (bounds) engine?.zoomToVectorBounds?.(bounds);
            }}
            data-testid={`${testId}-attribute-table`}
          />
        ) : null}

        {/* Edit / draw — gated by read-only */}
        <SectionBlock id={`${sectionId}-edit`}>
          <Stack direction="column" gap={1}>
            <Text variant="caption" as="h4" style={{ margin: 0, color: tokens.color.ink.secondary }}>
              Edit and draw
            </Text>
            <Stack direction="row" gap={2} style={{ flexWrap: 'wrap' }}>
              <PermissionButton
                readOnly={readOnly}
                icon="polyline"
                label="Draw"
                active={editTool === 'draw' || pendingEditTool === 'draw'}
                onClick={() => handleEditTool('draw')}
                testId={`${testId}-draw`}
              />
              <PermissionButton
                readOnly={readOnly}
                icon="edit"
                label="Modify"
                active={editTool === 'modify'}
                onClick={() => handleEditTool('modify')}
                testId={`${testId}-edit`}
              />
              <PermissionButton
                readOnly={readOnly}
                icon="crop_free"
                label="Scale"
                active={editTool === 'scale'}
                onClick={() => handleEditTool('scale')}
                testId={`${testId}-scale`}
              />
              {canUndoScale ? (
                <PermissionButton
                  readOnly={readOnly}
                  icon="replay"
                  label="Undo scale"
                  onClick={handleUndoScale}
                  testId={`${testId}-undo-scale`}
                />
              ) : null}
              <PermissionButton
                readOnly={readOnly}
                icon="delete"
                label="Delete"
                onClick={() => handleEditTool('delete')}
                testId={`${testId}-delete`}
              />
              <PermissionButton
                readOnly={readOnly}
                icon="gesture"
                label="Trace"
                active={editTool === 'trace' || pendingEditTool === 'trace'}
                onClick={() => handleEditTool('trace')}
                testId={`${testId}-trace`}
              />
              <Button
                variant="ghost"
                onClick={handleSwitchToMeasure}
                data-testid={`${testId}-measure`}
              >
                <Icon name="straighten" size={20} decorative />
                <span>Measure / buffer</span>
              </Button>
            </Stack>
            {editTool ? (
              <Text variant="caption" as="p" style={{ margin: 0, color: tokens.color.ink.secondary }}>
                {editTool === 'draw'
                  ? 'Click to place polygon vertices; click the first vertex or double-click to finish. Press Esc to cancel the current sketch.'
                  : editTool === 'modify'
                    ? 'Select a polygon, then drag a vertex or midpoint. Click away or switch tools when finished.'
                    : editTool === 'trace'
                      ? 'Click along existing polygon boundaries to snap vertices; double-click to finish.'
                      : 'Drag a scale handle to resize the selected polygon.'}
              </Text>
            ) : null}
            {editNotice ? (
              <Text variant="caption" as="p" role="status" style={{ margin: 0 }}>
                {editNotice} Export GeoJSON to save it outside this browser session.
              </Text>
            ) : null}
            {sessionEdits.length > 0 ? (
              <Stack
                direction="column"
                gap={1}
                data-testid={`${testId}-session-edits`}
              >
                <Stack direction="row" align="center" justify="space-between" gap={2}>
                  <Text
                    variant="caption"
                    as="h5"
                    id={`${sectionId}-session-edits-heading`}
                    style={{ margin: 0, color: tokens.color.ink.secondary }}
                  >
                    {`This session's edits (${sessionEdits.length})`}
                  </Text>
                  <Button
                    variant="ghost"
                    disabled={readOnly}
                    onClick={handleDeleteAllSessionEdits}
                    data-testid={`${testId}-session-edit-delete-all`}
                  >
                    <Icon name="delete" size={20} decorative />
                    <span>Delete all</span>
                  </Button>
                </Stack>
                <ul
                  aria-labelledby={`${sectionId}-session-edits-heading`}
                  style={{ listStyle: 'none', margin: 0, padding: 0 }}
                >
                  {sessionEdits.map((entry, index) => (
                    <li
                      key={entry.id == null ? `session-edit-${index}` : String(entry.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: tokens.space[2],
                        padding: `${tokens.space[1]} 0`,
                      }}
                    >
                      <Text variant="caption" as="span" style={{ margin: 0 }}>
                        {entry.label}
                      </Text>
                      <Stack direction="row" align="center" gap={1}>
                        <Tooltip content={`Zoom to ${entry.label}`}>
                          <IconButton
                            aria-label={`Zoom to ${entry.label}`}
                            onClick={() => handleZoomToSessionEdit(entry.feature)}
                            data-testid={`${testId}-session-edit-zoom-${index}`}
                          >
                            <Icon name="my_location" size={20} decorative />
                          </IconButton>
                        </Tooltip>
                        <Tooltip content={`Delete ${entry.label}`}>
                          <IconButton
                            aria-label={`Delete ${entry.label}`}
                            disabled={readOnly || entry.id == null}
                            onClick={() => {
                              if (entry.id == null) return;
                              handleDeleteSessionEdit(entry.id, entry.operation);
                            }}
                            data-testid={`${testId}-session-edit-delete-${index}`}
                          >
                            <Icon name="delete" size={20} decorative />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </li>
                  ))}
                </ul>
              </Stack>
            ) : null}
            {readOnly ? (
              <Stack direction="row" align="center" gap={1}>
                <Icon name="lock" size={20} decorative />
                <Text variant="caption" as="span">
                  Read-only by project permission. Switch role or open the layer in
                  edit mode to unlock these controls.
                </Text>
              </Stack>
            ) : null}
          </Stack>
        </SectionBlock>

        {/* Export selection */}
        <SectionBlock id={`${sectionId}-export`}>
          <Stack direction="column" gap={1}>
            <Text variant="caption" as="h4" style={{ margin: 0, color: tokens.color.ink.secondary }}>
              Export selection
            </Text>
            <Stack direction="row" gap={2} align="center" style={{ flexWrap: 'wrap' }}>
              <label
                style={{
                  fontFamily: tokens.font.family.ui,
                  fontSize: tokens.font.size.xs,
                  color: tokens.color.ink.secondary,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: tokens.space[1],
                }}
              >
                Format
                <select
                  aria-label="Export format"
                  value={exportFormat}
                  onChange={(event) => setExportFormat(event.target.value as VectorExportFormat)}
                  data-testid={`${testId}-export-format`}
                  style={selectStyle()}
                >
                  <option value="geojson">GeoJSON</option>
                  <option value="csv">CSV</option>
                  <option value="flatgeobuf">FlatGeobuf</option>
                </select>
              </label>
              <PermissionButton
                readOnly={false /* export is permitted in read-only */}
                icon="download"
                label="Export selection"
                onClick={handleExport}
                testId={`${testId}-export-action`}
              />
            </Stack>
            {exportNotice ? (
              <Banner
                severity="info"
                title="Export started"
                body={exportNotice}
                onDismiss={() => setExportNotice(null)}
                data-testid={`${testId}-export-notice`}
              />
            ) : null}
          </Stack>
        </SectionBlock>

        {/* OGC feature source info */}
        <SectionBlock id={`${sectionId}-source-info`}>
          <Stack direction="row" align="center" gap={2}>
            <Icon name="info" size={20} decorative />
            <Text variant="caption" as="h4" style={{ margin: 0, color: tokens.color.ink.secondary }}>
              Feature source info
            </Text>
          </Stack>
          <Stack direction="column" gap={1} style={{ marginTop: tokens.space[1] }}>
            <SourceRow label="Source kind" value={sourceInfo.kind} mono />
            <SourceRow label="Endpoint" value={sourceInfo.endpoint ?? 'Local / in-memory'} mono />
            <SourceRow
              label="Supported CRS"
              value={(sourceInfo.supportedCrs ?? [layer.crs]).join(', ')}
              mono
            />
            {sourceInfo.capabilities && sourceInfo.capabilities.length > 0 ? (
              <SourceRow
                label="Capabilities"
                value={sourceInfo.capabilities.join(', ')}
              />
            ) : null}
            <SourceRow label="Feature count" value={String(features.length)} mono />
          </Stack>
        </SectionBlock>
      </Stack>
    </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface SectionBlockProps {
  readonly id: string;
  readonly children: ReactNode;
}

function featureHighlightId(
  feature: VectorAttributeRow,
): string | number | null {
  if (typeof feature.id === 'string' || typeof feature.id === 'number') {
    return feature.id;
  }
  for (const key of [
    'OBJECTID_1',
    'OBJECTID',
    'objectid',
    'FID',
    'fid',
    'gid',
    'id',
    'ID',
  ]) {
    const value = feature.properties?.[key];
    if (typeof value === 'string' || typeof value === 'number') return value;
  }
  return null;
}

function SectionBlock({ id, children }: SectionBlockProps) {
  return (
    <section
      id={id}
      style={{
        background: tokens.color.surface[1],
        border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        borderRadius: tokens.radius.md,
        padding: tokens.space[3],
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[2],
      }}
    >
      {children}
    </section>
  );
}

interface FilterRowProps {
  readonly availableAttributes: ReadonlyArray<string>;
  readonly value: VectorFilter;
  readonly onChange: (next: VectorFilter) => void;
  readonly readOnly: boolean;
  readonly testId: string;
}

function FilterRow({ availableAttributes, value, onChange, readOnly, testId }: FilterRowProps) {
  return (
    <Stack direction="column" gap={1}>
      <Stack direction="row" align="center" gap={2}>
        <Icon name="filter_list" size={20} decorative />
        <Text variant="subtitle" as="h3" style={{ margin: 0 }}>
          Filter
        </Text>
      </Stack>
      <Stack direction="row" gap={2} style={{ flexWrap: 'wrap' }}>
        <select
          aria-label="Filter attribute"
          value={value.attribute ?? ''}
          disabled={readOnly}
          onChange={(event) =>
            onChange({
              ...value,
              attribute: event.target.value === '' ? null : event.target.value,
            })
          }
          data-testid={`${testId}-attribute`}
          style={selectStyle()}
        >
          <option value="">— Attribute —</option>
          {availableAttributes.map((attr) => (
            <option key={attr} value={attr}>
              {attr}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter operator"
          value={value.operator}
          disabled={readOnly || !value.attribute}
          onChange={(event) =>
            onChange({
              ...value,
              operator: event.target.value as VectorFilter['operator'],
            })
          }
          data-testid={`${testId}-operator`}
          style={selectStyle()}
        >
          <option value="equals">equals</option>
          <option value="contains">contains</option>
          <option value="isEmpty">is empty</option>
        </select>
        <input
          type="text"
          aria-label="Filter value"
          placeholder="value"
          value={value.value}
          disabled={readOnly || !value.attribute || value.operator === 'isEmpty'}
          onChange={(event) => onChange({ ...value, value: event.target.value })}
          data-testid={`${testId}-value`}
          style={inputStyle()}
        />
        <IconButton
          aria-label="Clear filter"
          onClick={() => onChange({ attribute: null, operator: 'equals', value: '' })}
          disabled={readOnly || !value.attribute}
          data-testid={`${testId}-clear`}
        >
          <Icon name="close" size={20} decorative />
        </IconButton>
      </Stack>
    </Stack>
  );
}

interface PermissionButtonProps {
  readonly readOnly: boolean;
  readonly icon: 'edit' | 'polyline' | 'download' | 'straighten' | 'crop_free' | 'delete' | 'gesture' | 'replay';
  readonly label: string;
  readonly onClick: () => void;
  readonly testId: string;
  readonly active?: boolean;
}

function PermissionButton({ readOnly, icon, label, onClick, testId, active = false }: PermissionButtonProps) {
  const button = (
    <Button
      variant={readOnly ? 'ghost' : active ? 'primary' : 'secondary'}
      disabled={readOnly}
      aria-disabled={readOnly}
      aria-pressed={active}
      onClick={onClick}
      data-testid={testId}
      style={{
        opacity: readOnly ? 0.6 : 1,
        cursor: readOnly ? 'not-allowed' : 'pointer',
      }}
    >
      <Icon name={readOnly ? 'lock' : icon} size={20} decorative />
      <span>{label}</span>
    </Button>
  );
  if (!readOnly) return button;
  return (
    <Tooltip content="Read-only by project permission">
      <span>{button}</span>
    </Tooltip>
  );
}

interface SourceRowProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}

function SourceRow({ label, value, mono }: SourceRowProps) {
  return (
    <Stack direction="row" gap={2} align="baseline">
      <Text
        variant="caption"
        as="span"
        style={{
          minWidth: tokens.layout.popoverSm,
          color: tokens.color.ink.secondary,
        }}
      >
        {label}
      </Text>
      <Text
        variant="body"
        as="span"
        style={{
          fontFamily: mono ? tokens.font.family.mono : tokens.font.family.ui,
          fontSize: tokens.font.size.sm,
        }}
      >
        {value}
      </Text>
    </Stack>
  );
}

function selectStyle() {
  return {
    minHeight: tokens.density.touchMin,
    background: tokens.color.surface[2],
    color: tokens.color.ink.primary,
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.input,
    paddingInline: tokens.space[2],
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  } as const;
}

function inputStyle() {
  return {
    minHeight: tokens.density.touchMin,
    background: tokens.color.surface[2],
    color: tokens.color.ink.primary,
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.input,
    paddingInline: tokens.space[2],
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  } as const;
}
